// react
import { SyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';

// mui
import { Container, Grid, Typography } from '@mui/material';

// i18n
import { useTranslation } from 'react-i18next';

// context
import {
	ContextProject,
	ContextSuppliers,
	ContextUserProjectPermissions,
} from '../Project.context';

// router
import { Outlet, Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';

// ui
import { Tabs } from '../../../ui/Tabs/Tabs';

// dialogs
import { DialogAddSupplier } from '../../../dialogs/DialogAddSupplier/DialogAddSupplier';
import { DialogUploadSuppliers } from '../../../dialogs/DialogUploadSuppliers/DialogUploadSuppliers';

export const Suppliers = () => {
	// hooks
	const { t } = useTranslation();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);
	const { refetchSuppliers } = useContext(ContextSuppliers);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// defaults
	const defaultTabValue = useMemo(() => {
		if (location.pathname.includes('documents')) {
			return 'documents';
		}
		return 'details';
	}, [location]);

	// state
	const [tabValue, setTabValue] = useState(defaultTabValue);

	// params
	const paramDialog = searchParams.get('dialog');

	// vars
	const urlBase = `/project/${project?.idWbg}/suppliers`;
	const tabs = [
		{
			component: RouterLink,
			label: t('details'),
			to: `${urlBase}/details`,
			value: 'details',
		},
	];

	if (userProjectPermissions?.document?.read) {
		tabs.push({
			component: RouterLink,
			label: t('documents'),
			to: `${urlBase}/documents`,
			value: 'documents',
		});
	}

	// handlers
	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			params.delete('dialogSupplierName');
			return params;
		});
	};

	// set active tab
	useEffect(() => {
		let tabValueByLocation = 'details';

		if (location.pathname.includes('documents')) {
			tabValueByLocation = 'documents';
		}
		setTabValue(tabValueByLocation);
	}, [location]);

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Container maxWidth={false}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography variant="h2">{t('suppliers')}</Typography>
							</Grid>
						</Grid>
					</Container>
				</Grid>
				<Grid item xs={12}>
					<Tabs hasBorder={false} onChange={handleChangeTab} tabs={tabs} value={tabValue} />
				</Grid>
				<Grid item xs={12}>
					<Outlet />
				</Grid>
			</Grid>
			<DialogUploadSuppliers
				isOpen={paramDialog === 'uploadSuppliers'}
				onClose={() => {
					setSearchParams((params) => {
						params.delete('dialog');
						return params;
					});
				}}
				onSuccess={() => {
					refetchSuppliers();
				}}
			/>
			<DialogAddSupplier
				isOpen={paramDialog === 'addSupplier'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					refetchSuppliers();
				}}
			/>
		</>
	);
};
